import React, { useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import {
  Divider,
  Grid,
  Link,
  Box,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Delete, Search, ContentCopy } from "@mui/icons-material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DOMPurify from 'dompurify';
import { UserContext } from "../../auth/auth-context";
import SearchIcon from '@mui/icons-material/Search';
import { CLONE_BOOKING_TYPE, REMOVE_BOOKING_TYPES } from "../../../graphql/mutations/bookingType";
import { useSelectedBusiness } from "../../../redux/store";
import { getValueFromKey } from "../../../util/meetingTypes";
import { Truncate } from "../../../util/truncate";
import ActionsContainer from "./bookingTypeActionContainer";
import { rootClientPath } from "../../../util/link";
import { getCategoryName, useIsMobile } from "../../../util/utils";
import ConfirmDialog from './confirmDialog';
import { usePermissions } from "../../../hooks/usePermissions";
import { useTokenLoader } from "../../../util/tokenLoader";
import GenericTooltip from "../../common/genericToolTip";


const tooltipData = {
  bookingType: "Booking types can represent services, events, or classes. For example, you can create a booking type like '20-minute meeting' under the event category, allowing your customers to easily book that time slot.",

};

const BookingType = (props) => {
  // useTokenLoader();
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser; 
  const isMobile = useIsMobile();
  const selectedBusiness = useSelectedBusiness();



  /* ──────────────────────────────────────────────── */
  /* New: build staff & business tip URLs            */
  /* ──────────────────────────────────────────────── */
  const bizSlug = selectedBusiness?.businessBasic.url;
  const staffSlug = providerUser?.url;
  const tipStaffUrl = `${rootClientPath}/${bizSlug}/${staffSlug}/tip`;
  const tipBusinessUrl = `${rootClientPath}/${bizSlug}/tip`;
  /* ------------------------------------------------ */

  const staffBookingTypeUrl = `${rootClientPath}/${bizSlug}/${staffSlug}`;
  const businessBookingPage = `${rootClientPath}/${bizSlug}`;



  const [isDiabled, setIsDisabled] = useState(false)
  const [createdBy, setCreatedBy] = useState(null)
  const { canEditBookingType } = usePermissions();

  useEffect(() => {
    const canEdit = canEditBookingType(Number(createdBy));
    canEdit ? setIsDisabled(false) : setIsDisabled(true)
  }, [canEditBookingType])

  // Remove useCloseConfirmation hook and use our own ConfirmDialog.
  // const { CancelConfirmationModal } = useCloseConfirmation({ isCloseWithOutConfirming: false });

  const [expanded, setExpanded] = useState([]); // Array to manage individual accordion states
  const [areAllExpanded, setAreAllExpanded] = useState(false);

  const [removeBookingType] = useMutation(REMOVE_BOOKING_TYPES);
  const [cloneBookingType] = useMutation(CLONE_BOOKING_TYPE);

  const [currentVal, setCurrentVal] = useState();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [filteredData, setFilteredData] = useState({
    1: props.data?.filter((val) => val.bookingTypeCategoryId === 1) || [],
    2: props.data?.filter((val) => val.bookingTypeCategoryId === 2) || [],
    3: props.data?.filter((val) => val.bookingTypeCategoryId === 3) || [],
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const cloneBType = (event, id) => {
    event.stopPropagation();
    cloneBookingType({ variables: { bookingTypeId: parseInt(id) } })
      .then((res) => {
        props.refetch().then(() => {
          navigate("/biz/update/" + res.data.cloneBookingType.id);
        });
      })
      .catch(console.error);
  };

  const removeBookingTypeFromTheList = (variables) => {
    removeBookingType({ variables })
      .then(() => {
        setFilteredData((prev) => ({
          ...prev,
          [activeCategory]: prev[activeCategory].filter(
            (item) => item.id !== variables.bookingTypeId
          )
        }));
        // Slight delay before refetching
        setTimeout(() => props.refetch(), 300);
      })
      .catch(console.error);
  };

  const handleYes = () => {
    if (currentVal && currentVal.id) {
      removeBookingTypeFromTheList({ bookingTypeId: parseInt(currentVal.id) });
      setShowCancelModal(false);
      setCurrentVal(null); // Reset currentVal after deletion
    } else {
      console.error("No valid data found for deletion");
    }
  };

  const handleSettingsClick = (event, val) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentVal(val); // Set the entire item data 
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearch(searchTerm);

    const filteredItems = props.data?.filter(
      (item) =>
        item.bookingTypeCategoryId === activeCategory &&
        item.title?.toLowerCase().includes(searchTerm)
    );

    setFilteredData((prev) => ({
      ...prev,
      [activeCategory]: filteredItems || []
    }));
  };

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    setAreAllExpanded(checked);

    if (checked) {
      // Expand all accordions
      const allPanels = filteredData[activeCategory].map((_, index) => `panel${index + 1}`);
      setExpanded(allPanels);
    } else {
      // Collapse all accordions
      setExpanded([]);
    }
  };

  // Update the "Expand all" checkbox based on individual accordion states.
  React.useEffect(() => {
    setAreAllExpanded(expanded.length === filteredData[activeCategory]?.length);
  }, [expanded, filteredData, activeCategory]);

  useEffect(() => {
    // If there are any booking types in the active category, set createdBy once.
    if (filteredData[activeCategory]?.length > 0) {
      setCreatedBy(filteredData[activeCategory][0]?.createdBy);
    }
  }, [filteredData, activeCategory]);

  const handleIndividualToggle = (panel) => {
    setExpanded((prevExpanded) => {
      if (prevExpanded.includes(panel)) {
        return prevExpanded.filter((p) => p !== panel); // Collapse if already expanded
      } else {
        return [...prevExpanded, panel]; // Expand if not expanded
      }
    });
  };

  const hasData = Object.values(filteredData).some((subArray) => subArray?.length > 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Typography variant="h5" sx={{ marginLeft: 2 }}>
          Booking Types  <GenericTooltip title={tooltipData.bookingType} />
        </Typography>


        <Box sx={{ pl: 2 }}>
          {/* Booking Links Accordion */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Booking Links</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Business:</Typography>
                <Tooltip title={businessBookingPage} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={businessBookingPage}
                      label="Copy Business Booking Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    />
                  </Box>
                </Tooltip>

                <Typography variant="caption" sx={{ fontWeight: 500 }}>Personal:</Typography>
                <Tooltip title={staffBookingTypeUrl} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={staffBookingTypeUrl}
                      label="Copy Personal Booking Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionDetails>
          </Accordion>
{/* 
          Tip Links Accordion
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Tip Links</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Business:</Typography>
                <Tooltip title={tipBusinessUrl} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={tipBusinessUrl}
                      label="Copy Business Tip Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    />
                  </Box>
                </Tooltip>

                <Typography variant="caption" sx={{ fontWeight: 500 }}>Personal:</Typography>
                <Tooltip title={tipStaffUrl} arrow placement="top-start">
                  <Box>
                    <ActionsContainer
                      url={tipStaffUrl}
                      label="Copy Personal Tip Link"
                      showLink
                      showLiveButton={false}
                      sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionDetails>
          </Accordion> */}

          <Divider sx={{ mt: 2 }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", padding: 2 }}>
          {[1, 2, 3].map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? "contained" : "outlined"}
              onClick={() => setActiveCategory(category)}
              sx={{ marginBottom: 1 }}
            >
              {getCategoryName(category)} ({filteredData[category]?.length || 0})
            </Button>
          ))}
        </Box>
      </Grid>

      {/* Main Content Area */}
      <Grid item xs={12} md={9} sx={{ ml: isMobile ? 2 : 0, mr: isMobile ? 2 : 0 }}>
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            width: "55%",
            marginBottom: "20px",
            padding: "8px 16px",
            borderRadius: "4px",
          }}
        >
          <Input
            disableUnderline
            value={search}
            onChange={handleSearch}
            placeholder="Filter..."
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
            sx={{ flex: 1 }}
          />
        </Paper>

        {/* Right-aligned Expand All */}
        {hasData && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={areAllExpanded}
                  onChange={handleToggleAll}
                  inputProps={{ "aria-label": "Toggle expand all" }}
                />
              }
              label={<Typography variant="body2">Expand all</Typography>}
              sx={{ marginLeft: "auto" }}
            />
          </Box>
        )}

        {/* Grid layout for displaying items */}
        <Grid
          container
          spacing={2}
          sx={{
            mt: -3,
            height: "auto",
            padding: 0,
          }}
        >
          {(activeCategory
            ? filteredData[activeCategory]
            : Object.values(filteredData).flat()
          )?.map((bookingType, index) => {
            return <Grid item xs={12} sm={6} md={4} key={index}>
              <Accordion
                expanded={expanded.includes(`panel${index + 1}`)}
                onChange={() => handleIndividualToggle(`panel${index + 1}`)}
                sx={{
                  borderRadius: "10px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 1,
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%" }}>
                    <Box sx={{ width: 4, height: 20, backgroundColor: bookingType.color }} />
                    <Typography>{Truncate(bookingType.title, 55)}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 2,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    <IconButton
                      onClick={(e) => handleSettingsClick(e, bookingType)}
                      aria-label={`settings-button-${bookingType.id}`}
                      aria-controls="settings-menu"
                      aria-haspopup="true"
                      sx={{
                        position: "absolute",
                        top: 70,
                        right: 20,
                        padding: 0,
                      }}
                    >
                      <SettingsIcon fontSize="small" sx={{ color: "#D3D3D3" }} />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    {getValueFromKey(bookingType.meetingLocationTypeId)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary", mb: 2 }}>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(Truncate(bookingType.desc, 100)) }} />
                  </Typography>
                  <Link
                    underline="hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/biz/update/${bookingType.id}`);
                    }}
                    sx={{ cursor: "pointer", color: "primary.main", display: "block", mb: 2 }}
                  >
                    See Details
                  </Link>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <ActionsContainer
                      url={`${rootClientPath}/${selectedBusiness?.businessBasic.url}/booking/${bookingType?.link}`}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          })}

          {/* Settings Menu */}
          <Menu
            id="settings-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              disabled={isDiabled}
              onClick={(e) => {
                e.stopPropagation();
                cloneBType(e, parseInt(currentVal.id));
                handleMenuClose();
              }}
            >
              <ContentCopy sx={{ color: "gray", mr: 1 }} /> Clone
            </MenuItem>
            <MenuItem
              disabled={isDiabled}
              onClick={(e) => {
                e.stopPropagation();
                // Open our own confirmation dialog for deletion.
                setShowCancelModal(true);
                handleMenuClose();
              }}
            >
              <Delete disabled={isDiabled} sx={{ color: "gray", mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </Grid>

        {/* Custom Deletion Confirmation Modal */}
        {showCancelModal && (
          <ConfirmDialog
            open={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            onCancel={() => setShowCancelModal(false)}
            onConfirm={() => handleYes(currentVal)}
            title="Confirm Deletion"
            bookingTitle={currentVal ? currentVal.title : ""}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default BookingType;
